import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { trackEvent } from '../lib/posthog'

interface UserProfile {
  name: string
  email: string
  state: string
  lhn: string
  defaultMode: 'clinic' | 'study'
  plan: string
  createdAt: string
  lastActive: string
}

export default function Settings() {
  const { user, signOut } = useAuth()
  const [profile, setProfile] = useState<UserProfile>({
    name: user?.name || '',
    email: user?.email || '',
    state: 'SA',
    lhn: 'CALHN',
    defaultMode: 'clinic',
    plan: 'trial',
    createdAt: '2026-04-20',
    lastActive: new Date().toISOString()
  })

  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const stateOptions = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' }
  ]

  const lhnOptions = {
    SA: [
      { value: 'CALHN', label: 'Central Adelaide LHN' },
      { value: 'NALHN', label: 'Northern Adelaide LHN' },
      { value: 'SALHN', label: 'Southern Adelaide LHN' },
      { value: 'CHSALHN', label: 'Country Health SA LHN' },
      { value: 'BNSALHN', label: 'Barossa Hills Fleurieu LHN' },
      { value: 'EYLHN', label: 'Eyre and Far North LHN' }
    ],
    NSW: [
      { value: 'SLHD', label: 'Sydney LHD' },
      { value: 'SESLHD', label: 'South Eastern Sydney LHD' },
      { value: 'SWSLHD', label: 'South Western Sydney LHD' }
    ],
    // Add more as needed
  } as Record<string, { value: string; label: string }[]>

  const handleSave = async () => {
    setIsSaving(true)
    setSaveStatus('idle')

    try {
      // Simulate API call - will be replaced with actual backend
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Track settings update
      trackEvent('settings_updated', {
        user_id: user?.id,
        field: 'state',
        new_value: profile.state
      })

      setSaveStatus('success')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error) {
      console.error('Save error:', error)
      setSaveStatus('error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleFieldChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({
      ...prev,
      [field]: value,
      // Reset LHN when state changes
      ...(field === 'state' ? { lhn: lhnOptions[value]?.[0]?.value || '' } : {})
    }))
  }

  const availableLhns = lhnOptions[profile.state] || []

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
              <p className="text-gray-600 mt-1">Manage your GPMeta preferences and profile</p>
            </div>
            <a
              href="/app"
              className="text-blue-600 hover:text-blue-700 flex items-center text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to App
            </a>
          </div>
        </div>

        <div className="space-y-6">
          {/* Account Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan
                </label>
                <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                  <span className="text-sm font-medium text-blue-800 capitalize">{profile.plan}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Member Since
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                  <span className="text-sm text-gray-700">
                    {new Date(profile.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Active
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                  <span className="text-sm text-gray-700">
                    {new Date(profile.lastActive).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Location Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Location Settings</h2>
            <p className="text-sm text-gray-600 mb-6">
              Your location affects which state-specific guidelines and Local Health Network resources are available.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State/Territory
                </label>
                <select
                  value={profile.state}
                  onChange={(e) => handleFieldChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {stateOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Local Health Network
                </label>
                <select
                  value={profile.lhn}
                  onChange={(e) => handleFieldChange('lhn', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={availableLhns.length === 0}
                >
                  <option value="">Select LHN (optional)</option>
                  {availableLhns.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {availableLhns.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">No LHN options available for {profile.state}</p>
                )}
              </div>
            </div>
          </div>

          {/* App Preferences */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">App Preferences</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Mode
              </label>
              <select
                value={profile.defaultMode}
                onChange={(e) => handleFieldChange('defaultMode', e.target.value)}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="clinic">Clinic (brief, mid-consult)</option>
                <option value="study">Study (longer, educational)</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Actions</h2>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`px-6 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  saveStatus === 'success'
                    ? 'bg-green-600 text-white'
                    : saveStatus === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } focus:ring-blue-500 disabled:opacity-50`}
              >
                {isSaving ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : 'Save Changes'}
              </button>

              <button
                onClick={handleSignOut}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}